/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import * as _ from 'underscore';
import { compose } from 'redux';
import Content from 'app/components/common/Content.jsx';
import CreateFeedHeader from 'app/components/Headers/CreateFeedHeader.jsx';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import ShortenerProvider from 'app/redux/Providers/ShortenerProvider.jsx';
import DomainProvider from 'app/redux/Providers/DomainProvider.jsx';
import PersistData from 'app/mixins/PersistData.jsx';
import Utilities from 'app/utility/Utilities.js';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import RouteSettingsScreen from '@/shadcn/components/settings/complex/route-settings.tsx';
import {
    buildRouteSections,
    ROUTE_COMPOSITE_PATHS,
    ROUTE_ERROR_KEYS
} from '@/lib/route-settings-sections.ts';
import {
    dirtySectionIds,
    sectionErrorCounts,
    sectionPaths,
    SETTINGS_NOTE_KEY,
    unchanged,
    validationKeys,
    validationTree
} from '@/lib/settings-sections.ts';
import { routeWrites } from '@/lib/settings-changes.ts';

/**
 * The tab a URL names, and the URL a tab wants. The four routes stay so deep
 * links keep working; the shell selects by `activeSection`. Unlike the source
 * and destination screens nothing is renamed — legacy's four segments are
 * already the four section ids.
 */
const SECTIONS = ['detail', 'filters', 'tracking', 'shorteners'];

/**
 * Whether the store holds ROUTE_SHOW's record of a route rather than ROUTE_LIST's summary, which
 * carries none of the settings this screen edits. A draft made from the summary would show them all
 * unset, and saving it would send no filter terms, which Route::update_api takes as no filter.
 */
function isShownRoute(route) {

    return route.igID !== undefined;

}

class RouteSettings extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            cleanRoute: {},
            route: {},
            errors: null,
            currentModal: null,
            noteDismissed: false,
            shown: false
        };

        this.saveRouteSettings = this.saveRouteSettings.bind(this);
        this.cancelAction = this.cancelAction.bind(this);
        this.selectSection = this.selectSection.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);
        this.dismissNote = this.dismissNote.bind(this);

    }

    static getDerivedStateFromProps(props, state) {
        let nextState = {};

        // a record already in the store can be an earlier visit's, changed since, so the draft waits for this one's show
        for (let i = 0; i < props.automatedRoutes.list.length; ++i) {
            if (props.automatedRoutes.list[i].id == props.router.params.routeId && state.shown && _.isEmpty(state.route) && isShownRoute(props.automatedRoutes.list[i])) {
                nextState.cleanRoute = props.automatedRoutes.list[i];
                nextState.route = Utilities.deepClone(props.automatedRoutes.list[i]);
                break;
            }
        }

        // follows the store, so the banner goes with the error: on a retry, and on leaving
        let errors = props.getApiErrors(['ROUTE_UPDATE']),
            error = errors.length ? errors[0] : null;
        if (error !== state.errors) {
            nextState.errors = error;
        }

        return (Object.keys(nextState).length) ? nextState : null;

    }

    componentDidMount() {

        this.props.getRoute(this.props.router.params.routeId);

        // The Shorteners tab's one select offers both lists, and the system
        // default option has to name whichever the account points at.
        // RouteShortenersPanel fetched them from its own constructor; the select
        // is built from data now, so the fetch moves up with it.
        this.props.getShorteners();
        this.props.getDomains();

        this.setState({
            noteDismissed: this.props.getPersistedData(SETTINGS_NOTE_KEY.route) === true
        });

    }

    componentWillUnmount() {

        // a refused save's errors describe this draft, and would greet the next visit
        this.props.deleteApiError(['ROUTE_UPDATE']);

    }

    componentDidUpdate(prevProps, prevState) {

        if (prevProps.app.loading.ROUTE_SHOW === true && this.props.app.loading.ROUTE_SHOW === false && !this.state.shown) {
            this.setState({shown: true});
        }

        // only a save leaves; the route's record also changes as the list and its show come in
        if (prevProps.app.loading.ROUTE_UPDATE === true && this.props.app.loading.ROUTE_UPDATE === false &&
            this.props.getApiErrors(['ROUTE_UPDATE']).length === 0) {
            this.props.router.navigate('/share/automate/detail/' + this.props.router.params.routeId);
        }

    }

    dismissNote() {

        this.props.persistData(SETTINGS_NOTE_KEY.route, true);
        this.setState({noteDismissed: true});

    }

    /** Which settings tab the current URL names. */
    currentSection() {

        let segment = this.props.router.location.pathname.split('/').pop();

        return SECTIONS.indexOf(segment) !== -1 ? segment : 'detail';

    }

    selectSection(id) {

        this.props.router.navigate([
            '/share/automate/settings',
            this.props.router.params.routeId,
            SECTIONS.indexOf(id) !== -1 ? id : 'detail'
        ].join('/'));

    }

    /**
     * Every field-level edit. `routeWrites` turns what the screen reported into
     * dot paths and values; each goes through the same
     * `FormUtilityMixin.requestChange` the legacy panels use, which mutates and
     * re-sets `this.state` in place — so the shortener select's pair of writes
     * compounds correctly across the loop.
     */
    onFieldChange(change) {

        routeWrites(change, this.state.route).forEach((entry) => {
            FormUtilityMixin.requestChange.call(this, {
                currentTarget: {dataset: {field: entry.field}},
                target: {value: entry.value}
            });
        });

    }

    saveRouteSettings() {

        this.props.updateRoute(this.state.route);

    }

    cancelAction() {

        this.props.router.navigate('/share/automate/detail/' + this.props.router.params.routeId);

    }

    render() {

        let route = this.state.route,
            routeId = (route.id) ? route.id : 0,
            loading = _.isEmpty(route),
            sections = buildRouteSections(route, {
                constants: this.props.app.config.constants,
                shorteners: this.props.shorteners || [],
                domains: this.props.domains || [],
                shortenServiceName: ConstantMixin.getShortenServiceByCode,
                defaultSaId: this.props.user.detail.defaultSaId,
                defaultPdId: this.props.user.detail.defaultPdId,
                validation: validationTree(this.props.app),
            }),
            paths = sectionPaths(sections, 'route', ROUTE_COMPOSITE_PATHS),
            settingsTab = (
                <div className="editor-title-tabs">
                    <div className="editor-title-tab tab-active">
                        <i className="fa fa-cog"></i> Settings
                    </div>
                </div>
            ),
            header = (
                <CreateFeedHeader
                    {...this.props}
                    section={route.name}
                    sectionLink={"/share/automate/detail/" + routeId}
                    action="Settings"
                    tabs={settingsTab} />
            );

        return (
            <Content
                header={header}
                main={
                    <div className="tw-root">
                        <RouteSettingsScreen
                            sections={sections}
                            routeName={route.name || ''}
                            sourceCount={_.isArray(route.inputs) ? route.inputs.length : 0}
                            loading={loading}
                            formKey={String(routeId)}
                            onChange={this.onFieldChange}
                            activeSection={this.currentSection()}
                            onSectionChange={this.selectSection}
                            dirty={!unchanged(route, this.state.cleanRoute)}
                            dirtySections={dirtySectionIds(paths, route, this.state.cleanRoute)}
                            errorCounts={sectionErrorCounts(validationKeys(this.props.app), ROUTE_ERROR_KEYS)}
                            saving={this.props.app.loading.ROUTE_UPDATE === true}
                            formError={this.state.errors !== null
                                ? "We couldn't complete that update. Check the highlighted tabs for details."
                                : undefined}
                            onBack={this.cancelAction}
                            note={this.state.noteDismissed ? null : undefined}
                            onDismissNote={this.dismissNote}
                            /*
                             * NO HEADER ACTIONS. The screen defaults to an
                             * Error log button, and this level has nowhere to
                             * send it: legacy's route settings offers no log,
                             * and the per-source History and Medic belong to a
                             * source rather than to the route above it. An
                             * empty list is the honest answer; a button landing
                             * on the route detail would be worse than none.
                             */
                            actions={[]}
                            onSave={this.saveRouteSettings}
                            onCancel={this.cancelAction} />
                    </div>
                } />
        );
    }

};

RouteSettings.displayName = "RouteSettings";

const enhance = compose(
    ComponentWithContext,
    PersistData,
    ShortenerProvider,
    DomainProvider
);

export default enhance(RouteSettings);
